// ----------------------------------------------------------------------
// <copyright file="Izenda.UserAccount.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------
import { observable, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import PagingModel from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import { DEFAULT_VALUE } from 'logic/data/Izenda.UserAccountConstants';
import UserAccountDetail from './Izenda.UserAccountDetail';
import { concatArray } from 'share/utils/Izenda.LazyLoadingUtils';
import uiState from 'logic/Izenda.UiState';
import { every, reject, map, flow, head, get, filter, isEmpty, forEach, remove } from 'lodash/fp';

@tracker
class UserAccount {
  constructor(data) {
    data && this.populateData(data);
  }
  @observable pageInfo = new PagingModel(DEFAULT_VALUE.SORT_DIRS, DEFAULT_VALUE.SEARCH_CRITERIA);
  @observable userAccountList = [];
  @observable filterText = null;
  @observable roleList = [];
  @observable languageList = [];
  @observable dateFormatList = [];
  @observable isLastPage = false;

  @computed
  get hasSelectedAllUser() {
    if (isEmpty(this.userAccountList)) {
      return false;
    }
    return every(['selected', true], this.userAccountList);
  }
  @computed
  get filterUser() {
    return reject('deleted', this.userAccountList);
  }

  /**
   * Populate data
   * @param  {Object} user account object
   */
  @action
  populateData(data) {
    this.userAccountList = data.userAccountList;
  }

  /**
   * Populate data
   * @param  {Object} List data of user account detail
   */
  @action
  populateUserAccountList(userAccount) {
    this.userAccountList = map(userAccountDetail => new UserAccountDetail(userAccountDetail), userAccount.result);
    this.pageInfo.totalRecords = userAccount.total;
  }

  /**
   * Populate partial data for lazy loading
   * @param  {Object} result user lazy loading list result
   * @param  {boolean} isSearching
   */
  @action
  populateLazyUserAccountList({ result, isLastPage, total }, isSearching) {
    this.isLastPage = isLastPage;
    this.pageInfo.totalRecords = total;

    isSearching && (this.userAccountList = []);

    let newUsers = map(userAccountDetail => new UserAccountDetail(userAccountDetail), result);
    const selectedTenantId = uiState.getTenantId();
    const userTenantId = flow(head, get('tenantId'))(newUsers);

    userTenantId === selectedTenantId && concatArray(this.userAccountList, newUsers, 'id', !isLastPage);

    return this.userAccountList;
  }

  /**
   * Check user account dirty or not
   * @return {boolean} result of dirty
   */
  @action
  checkDirty() {
    var dirtyItems = filter(item => item.checkDirty && item.checkDirty(), this.userAccountList);

    return !isEmpty(dirtyItems);
  }

  /**
   * reset all selected user
   */
  @action
  resetAllSelected() {
    forEach(item => {
      if (item.selected) {
        item.updateSelected(false);
      }
    }, this.userAccountList);
  }

  /**
   * Update filter text   
   * @return {string} filterText
   */
  @action
  updateFilterText(filterText) {
    this.filterText = filterText;
  }

  /**
   * populate Role List 
   * @param  {array} roles 
   */
  @action
  populateRoleList(roles) {
    this.roleList = roles;
  }

  /**
   * populate Language List
   * @param  {array} languages
   */
  @action
  populateLanguageList(languages) {
    this.languageList = languages;
  }

  /**
   * populate Date Format List
   * @param  {array} dateFormats
   */
  populateDateFormatList(dateFormats) {
    this.dateFormatList = map(item => {
      return {
        labelDateFormat: item,
        dateFormat: item
      };
    }, dateFormats);
  }

  /**
   * Delete user
   * @param  {Object} user
   */
  @action
  deleteUser(user) {
    this.userAccountList = remove({ id: user.id }, this.userAccountList);
  }

  /**
   * Add new user
   * @param  {Object} user
   */
  addUser(user) {
    const newUserDetail = new UserAccountDetail(user);
    concatArray(this.userAccountList, [newUserDetail], 'id');

    return newUserDetail;
  }
}

export default UserAccount;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.UserAccount.js